import { useEffect, useState } from "react";
import { API_BASE, AUTH_STORAGE_KEY } from "../config/appConfig";
import { normalizeErrorMessage } from "../utils/errors";

export default function AuthScreen({ onAuthenticated }) {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Password reset confirm state
  const [resetUid, setResetUid] = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activation = params.get("activation");
    const passwordResetUid = params.get("password_reset_uid");
    const passwordResetToken = params.get("password_reset_token");

    if (passwordResetUid && passwordResetToken) {
      setResetUid(passwordResetUid);
      setResetToken(passwordResetToken);
      setMode("reset");
      params.delete("password_reset_uid");
      params.delete("password_reset_token");
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
      return;
    }

    if (!activation) return;

    if (activation === "success") {
      setNotice("Konto zostało aktywowane. Możesz się teraz zalogować.");
    } else if (activation === "already_active") {
      setNotice("Konto jest już aktywne. Możesz się zalogować.");
    } else if (activation === "invalid") {
      setError("Link aktywacyjny jest nieprawidłowy lub wygasł.");
    }

    params.delete("activation");
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, []);

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!email.trim()) {
      setError("Podaj adres email.");
      return;
    }

    setLoading(true);
    try {
      await fetch(`${API_BASE}/auth/users/reset_password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Always show success to avoid email enumeration
      setNotice("Jeśli konto z tym adresem email istnieje, wysłaliśmy link do resetowania hasła.");
      setEmail("");
    } catch {
      setError("Brak połączenia z serwerem. Sprawdź swoje połączenie internetowe i spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!password) {
      setError("Podaj nowe hasło.");
      return;
    }
    if (password !== repeatPassword) {
      setError("Hasła nie są takie same.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/users/reset_password_confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: resetUid,
          token: resetToken,
          new_password: password,
          re_new_password: repeatPassword,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        setError(normalizeErrorMessage(data));
        return;
      }

      setMode("login");
      setPassword("");
      setRepeatPassword("");
      setNotice("Hasło zostało zmienione. Możesz się teraz zalogować.");
    } catch {
      setError("Brak połączenia z serwerem. Sprawdź swoje połączenie internetowe i spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!username.trim() || !password) {
      setError("Podaj nazwę użytkownika i hasło.");
      return;
    }

    if (mode === "signup" && password !== repeatPassword) {
      setError("Hasła nie są takie same.");
      return;
    }

    setLoading(true);

    try {
      if (mode === "signup") {
        const signupPayload = {
          username: username.trim(),
          password,
          re_password: repeatPassword,
        };
        if (email.trim()) signupPayload.email = email.trim();

        const signupResponse = await fetch(`${API_BASE}/auth/users/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signupPayload),
        });

        if (!signupResponse.ok) {
          const signupData = await signupResponse.json().catch(() => null);
          setError(normalizeErrorMessage(signupData));
          return;
        }

        setMode("login");
        setNotice(
          "Konto utworzone. Sprawdź email aktywacyjny i zaloguj się po aktywacji."
        );
        setPassword("");
        setRepeatPassword("");
        return;
      }

      const loginResponse = await fetch(`${API_BASE}/auth/jwt/create/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      const loginData = await loginResponse.json().catch(() => null);

      if (!loginResponse.ok || !loginData?.access) {
        setError(normalizeErrorMessage(loginData));
        return;
      }

      const auth = {
        username: username.trim(),
        access: loginData.access,
        refresh: loginData.refresh || null,
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
      onAuthenticated(auth);
    } catch {
      setError(
        "Brak połączenia z serwerem. Sprawdź swoje połączenie internetowe i spróbuj ponownie."
      );
    } finally {
      setLoading(false);
    }
  };

  const modeTitle = {
    login: "Logowanie",
    signup: "Rejestracja",
    forgot: "Reset hasła",
    reset: "Nowe hasło",
  }[mode];

  // --- forgot password form ---
  if (mode === "forgot") {
    return (
      <div
        style={{
          fontFamily: "var(--font-body)",
          background: "var(--color-bg-app)",
          minHeight: "100dvh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 16,
        }}
      >
        <form
          onSubmit={submitForgot}
          style={{
            width: "100%",
            maxWidth: 420,
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-lg)",
            padding: "var(--space-6)",
          }}
        >
          <div style={{ marginBottom: 18 }}>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-h1)",
                color: "var(--color-text-primary)",
                fontWeight: "var(--font-semibold)",
              }}
            >
              EvTask
            </h1>
            <p style={{ margin: "8px 0 0", color: "var(--color-text-muted)", fontSize: "var(--text-body)" }}>
              {modeTitle}
            </p>
          </div>

          {notice && (
            <div
              style={{
                background: "var(--color-success-bg)",
                border: "1px solid color-mix(in srgb, var(--color-success) 28%, white)",
                color: "var(--color-success-dark)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                marginBottom: 12,
                fontSize: "var(--text-sm)",
              }}
            >
              {notice}
            </div>
          )}

          {error && (
            <div
              style={{
                background: "var(--color-danger-bg)",
                border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
                color: "var(--color-danger-dark)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                marginBottom: 12,
                fontSize: "var(--text-sm)",
              }}
            >
              {error}
            </div>
          )}

          <p style={{ margin: "0 0 16px", color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>
            Podaj adres email przypisany do konta. Wyślemy link umożliwiający ustawienie nowego hasła.
          </p>

          <label style={{ display: "block", marginBottom: 16 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Email
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              style={{
                width: "100%",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                fontSize: "var(--text-body)",
                boxSizing: "border-box",
              }}
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              border: "none",
              background: "var(--color-primary)",
              color: "var(--color-white)",
              borderRadius: "var(--radius-md)",
              padding: "11px 12px",
              fontWeight: "var(--font-medium)",
              fontSize: "var(--text-body)",
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Wysyłanie..." : "Wyślij link resetujący"}
          </button>

          <button
            type="button"
            onClick={() => { setMode("login"); setError(""); setNotice(""); }}
            style={{
              width: "100%",
              marginTop: 10,
              border: "1px solid var(--btn-secondary-border)",
              background: "var(--color-bg-card)",
              color: "var(--btn-secondary-text)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontWeight: "var(--font-medium)",
              fontSize: "var(--text-body)",
              cursor: "pointer",
            }}
          >
            Wróć do logowania
          </button>
        </form>
      </div>
    );
  }

  // --- set new password form ---
  if (mode === "reset") {
    return (
      <div
        style={{
          fontFamily: "var(--font-body)",
          background: "var(--color-bg-app)",
          minHeight: "100dvh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 16,
        }}
      >
        <form
          onSubmit={submitReset}
          style={{
            width: "100%",
            maxWidth: 420,
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-lg)",
            padding: "var(--space-6)",
          }}
        >
          <div style={{ marginBottom: 18 }}>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-h1)",
                color: "var(--color-text-primary)",
                fontWeight: "var(--font-semibold)",
              }}
            >
              EvTask
            </h1>
            <p style={{ margin: "8px 0 0", color: "var(--color-text-muted)", fontSize: "var(--text-body)" }}>
              {modeTitle}
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "var(--color-danger-bg)",
                border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
                color: "var(--color-danger-dark)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                marginBottom: 12,
                fontSize: "var(--text-sm)",
              }}
            >
              {error}
            </div>
          )}

          <label style={{ display: "block", marginBottom: 10 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Nowe hasło
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              style={{
                width: "100%",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                fontSize: "var(--text-body)",
                boxSizing: "border-box",
              }}
            />
          </label>

          <label style={{ display: "block", marginBottom: 16 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Powtórz nowe hasło
            </div>
            <input
              type="password"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              autoComplete="new-password"
              style={{
                width: "100%",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                fontSize: "var(--text-body)",
                boxSizing: "border-box",
              }}
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              border: "none",
              background: "var(--color-primary)",
              color: "var(--color-white)",
              borderRadius: "var(--radius-md)",
              padding: "11px 12px",
              fontWeight: "var(--font-medium)",
              fontSize: "var(--text-body)",
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Zapisywanie..." : "Ustaw nowe hasło"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        background: "var(--color-bg-app)",
        minHeight: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-6)",
        }}
      >
        <div style={{ marginBottom: 18 }}>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-h1)",
              color: "var(--color-text-primary)",
              fontWeight: "var(--font-semibold)",
            }}
          >
            EvTask
          </h1>
          <p style={{ margin: "8px 0 0", color: "var(--color-text-muted)", fontSize: "var(--text-body)" }}>
            {modeTitle}
          </p>
        </div>

        {notice && (
          <div
            style={{
              background: "var(--color-success-bg)",
              border: "1px solid color-mix(in srgb, var(--color-success) 28%, white)",
              color: "var(--color-success-dark)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              marginBottom: 12,
              fontSize: "var(--text-sm)",
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "var(--color-danger-bg)",
              border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
              color: "var(--color-danger-dark)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              marginBottom: 12,
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </div>
        )}

        <label style={{ display: "block", marginBottom: 10 }}>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
            Nazwa użytkownika
          </div>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            style={{
              width: "100%",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontSize: "var(--text-body)",
              boxSizing: "border-box",
            }}
          />
        </label>

        {mode === "signup" && (
          <label style={{ display: "block", marginBottom: 10 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Email
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              style={{
                width: "100%",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                fontSize: "var(--text-body)",
                boxSizing: "border-box",
              }}
            />
          </label>
        )}

        <label style={{ display: "block", marginBottom: mode === "login" ? 6 : 10 }}>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
            Hasło
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            style={{
              width: "100%",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontSize: "var(--text-body)",
              boxSizing: "border-box",
            }}
          />
        </label>

        {mode === "login" && (
          <div style={{ textAlign: "right", marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => { setMode("forgot"); setError(""); setNotice(""); setEmail(""); }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "var(--text-xs)",
                color: "var(--color-primary)",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Zapomniałem hasła
            </button>
          </div>
        )}

        {mode === "signup" && (
          <label style={{ display: "block", marginBottom: 16 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Powtórz hasło
            </div>
            <input
              type="password"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              autoComplete="new-password"
              style={{
                width: "100%",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "10px 12px",
                fontSize: "var(--text-body)",
                boxSizing: "border-box",
              }}
            />
          </label>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%",
            border: "none",
            background: "var(--color-primary)",
            color: "var(--color-white)",
            borderRadius: "var(--radius-md)",
            padding: "11px 12px",
            fontWeight: "var(--font-medium)",
            fontSize: "var(--text-body)",
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading
            ? "Przetwarzanie..."
            : mode === "login"
            ? "Zaloguj się"
            : "Utwórz konto"}
        </button>

        <button
          type="button"
          onClick={() => {
            setMode((m) => (m === "login" ? "signup" : "login"));
            setError("");
            setNotice("");
            setPassword("");
            setRepeatPassword("");
          }}
          style={{
            width: "100%",
            marginTop: 10,
            border: "1px solid var(--btn-secondary-border)",
            background: "var(--color-bg-card)",
            color: "var(--btn-secondary-text)",
            borderRadius: "var(--radius-md)",
            padding: "10px 12px",
            fontWeight: "var(--font-medium)",
            fontSize: "var(--text-body)",
            cursor: "pointer",
          }}
        >
          {mode === "login"
            ? "Nie masz konta? Zarejestruj się"
            : "Masz konto? Zaloguj się"}
        </button>
      </form>
    </div>
  );
}

